// Official browser build. CRA/webpack wraps the CJS/ESM entry so `new WebPhone()`
// throws "WebPhone is not a constructor".
import '../../node_modules/ringcentral-web-phone/dist/index.iife.js';

function getWebPhoneCtor() {
  const ctor = typeof window !== 'undefined' ? window.WebPhone : null;
  if (typeof ctor === 'function') return ctor;
  throw new Error('RingCentral WebPhone SDK failed to load. Refresh the page and click Call again.');
}

let webPhone = null;
let activeSession = null;
let remoteAudio = null;

function ensureRemoteAudio() {
  if (!remoteAudio) {
    remoteAudio = document.createElement('audio');
    remoteAudio.autoplay = true;
    remoteAudio.setAttribute('playsinline', 'true');
    remoteAudio.style.display = 'none';
    document.body.appendChild(remoteAudio);
  }
  return remoteAudio;
}

function attachRemoteMedia(session) {
  if (!session) return;
  const audio = ensureRemoteAudio();
  const applyStream = (stream) => {
    if (stream) audio.srcObject = stream;
  };
  applyStream(session.mediaStream);
  if (typeof session.on === 'function') {
    session.on('mediaStreamSet', applyStream);
  }
  if (session.audioElement) {
    try {
      session.audioElement.autoplay = true;
      session.audioElement.play?.();
    } catch {
      // Browser may already be playing via the SDK element.
    }
  }
  audio.play?.().catch(() => {});
}

export function toWebPhoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

/** Callee digits for a US RingCentral account. Only US numbers are permitted. */
export function toWebPhoneCallee(value) {
  const digits = toWebPhoneDigits(value);
  if (!digits) return '';
  if (digits.length === 11 && digits.startsWith('1')) return digits;
  if (digits.length === 10) return `1${digits}`;
  return '';
}

export function describeMicError(err) {
  const name = String(err?.name || '');
  const message = String(err?.message || err || '');
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Microphone access was denied. Allow the microphone for this site and click Call again.';
  }
  if (name === 'NotFoundError' || /requested device not found/i.test(message)) {
    return 'No microphone was found. Plug in a headset or enable a microphone in Windows Sound settings, then click Call again.';
  }
  if (name === 'NotReadableError') {
    return 'The microphone is in use by another app, or Windows blocked it. Close other apps using the mic and try again.';
  }
  return message || 'Could not access the microphone.';
}

export async function requestMicrophone() {
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      code: 'unsupported',
      message: 'This browser does not support microphone calling. Use Chrome or Edge.',
    };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return { ok: true };
  } catch (err) {
    const denied = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';
    const missing = err.name === 'NotFoundError' || /requested device not found/i.test(String(err.message || ''));
    return {
      ok: false,
      code: denied ? 'denied' : (missing ? 'not_found' : 'error'),
      message: describeMicError(err),
    };
  }
}

export async function startWebPhone(sipInfo) {
  await disposeWebPhone();
  const WebPhone = getWebPhoneCtor();
  webPhone = new WebPhone({ sipInfo });
  await webPhone.start();
  return webPhone;
}

export function placeOutboundCall(calleeDigits, callerIdDigits, handlers = {}) {
  if (!webPhone) {
    throw new Error('Browser phone is not connected');
  }

  webPhone.once('outboundCall', (session) => {
    activeSession = session;
    attachRemoteMedia(session);
    handlers.onCalling?.(session);
    session.once('failed', (message) => {
      handlers.onFailed?.(message || 'The outbound call failed.');
    });
    const tryRecord = () => {
      if (typeof session.startRecording !== 'function') return;
      session.startRecording().catch((err) => {
        console.warn('RingCentral startRecording failed:', err?.message || err);
      });
    };
    session.once('answered', () => {
      handlers.onAnswered?.(session);
      tryRecord();
    });
    // SIP "answered" can fire before the patient picks up. Retry so cloud recording exists for RingSense.
    const recordRetry = setTimeout(tryRecord, 8000);
    session.once('disposed', () => {
      clearTimeout(recordRetry);
      handlers.onEnded?.(session);
      if (activeSession === session) activeSession = null;
    });
  });

  return webPhone.call(calleeDigits, callerIdDigits || undefined);
}

export async function hangupActiveCall() {
  const session = activeSession;
  if (!session) return;
  try {
    if (typeof session.hangup === 'function') {
      await session.hangup();
    }
  } catch {
    // Already ended.
  } finally {
    if (activeSession === session) activeSession = null;
  }
}

export function hasActiveCall() {
  return Boolean(activeSession);
}

export async function disposeWebPhone() {
  try {
    await hangupActiveCall();
  } catch {
    // ignore
  }
  if (webPhone) {
    try {
      await webPhone.dispose();
    } catch {
      // ignore
    }
  }
  webPhone = null;
  activeSession = null;
  if (remoteAudio) {
    remoteAudio.srcObject = null;
  }
}
